import { useMemo, useState, useEffect, createRef, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import tw from 'twin.macro';
import ReactDatePicker from 'react-datepicker';
import { isNumber } from 'lodash';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { Button } from '@components/Button';
import { Input } from '@insights_module/components/Input';
import { Spacer } from '@components/Spacer';
import { PlusIcon } from '@icons/PlusIcon';
import { MinusIcon } from '@icons/MinusIcon';
import {
  ROOT_SEGMENT_ID,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { SegmentCreator } from '@insights_module/components/SegmentCreator';
import { AQLFlatQueryBuilder } from '@insights_module/adc_ql/QueryBuilder';
import {
  buildQueryFromQueryData,
  groupQueryDataByGroupId,
  createQueryDataFromQuery,
} from '@insights_module/adc_ql/utils';
import { useNotification } from '@insights_module/hooks/useNotification';
import { Loader } from '@components/Loader';
import { createIntegrationTag } from '@insights_module/services/megaphone';
import {
  CreateTagFormType,
  CreateTagParamsType,
  IntegrationType,
} from '@insights_module/types/Integrations';
import { useIntegrationTags } from '@insights_module/hooks/useIntegrationTags';
import {
  INTEGRATION_QUERY_KEYS,
  INTEGRATION_TYPE_MAPPER,
} from '@insights_module/constants/integrations';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { CalendarIcon } from '@icons/CalendarIcon';
import { DatePicker } from '@components/DatePicker';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { addDaysToDate } from '@utils/dateUtils';
import {
  useFlightpathAvails,
  AdPlacementLogicType,
} from '@insights_module/hooks/useFlightpathAvails';

const ButtonsWrapper = tw.div`flex items-center justify-end gap-2`;
const ControlButton = tw(
  Button.Primary
)`text-xs leading-none px-3 py-[5px] w-auto h-[28px] border border-solid border-gray-1 align-middle shadow-none whitespace-nowrap`;
const Form = tw.form`w-full bg-page-background rounded-b-lg text-left`;
const FormInnerWrapper = tw.div`flex`;
const FormInputsWrapper = tw.div`flex flex-col w-full`;
const FormLabel = tw.label`text-xs font-semibold text-text-and-icon-muted`;
const TextArea = tw.textarea`w-full bg-page-background p-3 border border-solid border-gray-1 rounded text-sm text-text-and-icon autofill:text-accent-muted autofill:bg-page-background placeholder:text-text-and-icon-muted focus:text-text-and-icon focus:border-text-and-icon focus:outline-none`;
const FormInputsContainer = tw.div`w-full p-6 bg-page-background`;
const FormInputs = tw.div`w-full px-4`;
const FormControlsContainer = tw.div`w-full flex items-center justify-end gap-[8px] border-t border-solid border-gray-1 px-6 py-2`;
const AddDescriptionButton = tw.button`flex items-center justify-center text-xs font-semibold text-text-and-icon-muted leading-[18px]`;
const Subtitle = tw.span`text-sm text-text-and-icon text-left font-semibold w-full h-[38px] py-[10px] px-[24px] border-b border-gray-1 text-text-and-icon`;

const campaignDurationOptions: IDropdownOption[] = [
  { value: '30-days', label: <Option>Next 30 days</Option> },
  { value: '60-days', label: <Option>Next 60 days</Option> },
  { value: '90-days', label: <Option>Next 90 days</Option> },
  {
    value: 'custom',
    label: (
      <Option style={{ display: 'flex', justifyContent: 'space-between' }}>
        Custom <CalendarIcon stroke='var(--text-and-icon)' />{' '}
      </Option>
    ),
  },
];

const insertionOptions: IDropdownOption[] = [
  { value: 'pre', label: <Option>Pre</Option> },
  { value: 'mid', label: <Option>Mid</Option> },
  { value: 'post', label: <Option>Post</Option> },
  { value: 'pre-mid', label: <Option>Pre + Mid</Option> },
  { value: 'mid-post', label: <Option>Mid + Post</Option> },
  { value: 'pre-post', label: <Option>Pre + Post</Option> },
  { value: 'pre-mid-post', label: <Option>Pre + Mid + Post</Option> },
  { value: 'pre-mid_or', label: <Option>Pre or Mid</Option> },
  { value: 'mid-post_or', label: <Option>Mid or Post</Option> },
  { value: 'pre-post_or', label: <Option>Pre or Post</Option> },
  { value: 'pre-mid-post_or', label: <Option>Pre or Mid or Post</Option> },
];

export const CreateTagForm = ({ type }: { type: IntegrationType }) => {
  const { isEnabled: isFlightpathEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_FLIGHTPATH_INTEGRATION
  );
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notify, NTFC_TYPE } = useNotification();
  const { segmentsData, setSegmentsData, totalEpisodeCount, isTotalEpisodesCountLoading } =
    useSegmentCreatorContext();
  const { setIsCreateTagModalOpen, tagToDuplicate, setTagToDuplicate, searchTerm } =
    useIntegrationsContext();
  const { getTagById } = useIntegrationTags(type, searchTerm);
  const cloneTag = getTagById(tagToDuplicate);
  const { initiateFlightpathAvails, totalAvails, isLoading } = useFlightpathAvails();

  const datePickerRef = createRef<ReactDatePicker>();

  const [campaignDuration, setCampaignDuration] = useState<IDropdownOption>(
    campaignDurationOptions[0]
  );
  const [insertion, setInsertion] = useState<IDropdownOption>(insertionOptions[0]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentParams, setCurrentParams] = useState<string | null>(null);
  const [lastSentParams, setLastSentParams] = useState<string | null>(null);
  const [capDaysInterval, setCapDaysInterval] = useState<string | undefined>(undefined);
  const [capMaxImpressions, setCapMaxImpressions] = useState<string | undefined>(undefined);

  const createTagSchema = useMemo(() => {
    return yup.object().shape({
      title: yup
        .string()
        .max(
          255,
          t(
            'insights.integrations.megaphone.create_tag_modal.validation_error.name_max_length'
          ) as string
        )
        .required(
          t(
            'insights.integrations.megaphone.create_tag_modal.validation_error.name_required'
          ) as string
        ),
    });
  }, [t]);

  const [isDescriptionVisible, setIsDescriptionVisible] = useState<boolean>(false);
  const { register, handleSubmit, formState, setValue, setError } = useForm<CreateTagFormType>({
    resolver: yupResolver(createTagSchema),
  });

  const { isSubmitting, errors } = formState;

  const onSubmit: SubmitHandler<CreateTagFormType> = async (formData) => {
    const { title, description } = formData;

    // IHM requested enabling the creation of tags with the already existing title.
    // const tagWithEqualName = existingTags?.find(
    //   (item: TagType) => item.query.display_name === title
    // );
    // if (tagWithEqualName) {
    //   setError('title', {
    //     type: 'custom',
    //     message: t(
    //       'insights.integrations.megaphone.create_tag_modal.validation_error.name_already_exist',
    //       { name: title }
    //     ),
    //   });
    //   return;
    // }

    const builder = new AQLFlatQueryBuilder();
    buildQueryFromQueryData(groupQueryDataByGroupId(segmentsData), builder, null);
    const query = builder.build();

    const params: CreateTagParamsType = {
      display_name: title.trim(),
      description,
      aql_query: query,
      integrations: [
        {
          type: INTEGRATION_TYPE_MAPPER[type],
          extra_fields: {
            ad_tag_name: title.trim(),
          },
        },
      ],
    };

    try {
      const response = await createIntegrationTag(params);

      queryClient.invalidateQueries([INTEGRATION_QUERY_KEYS.INFINITE_TAGS]);
      setIsCreateTagModalOpen(false);
      handleCloseModal();
      notify({
        type: NTFC_TYPE.INFO,
        message: t('insights.integrations.megaphone.create_tag_modal.notification', {
          tagName: response.data.display_name,
        }),
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown | any) {
      if (error?.status === 409) {
        setError('title', {
          type: 'custom',
          message: t(
            'insights.integrations.megaphone.create_tag_modal.validation_error.name_already_exist',
            { name: title }
          ),
        });
      } else {
        if (error instanceof Error) {
          notify({
            type: NTFC_TYPE.DANGER,
            message: (error as Error).message,
          });
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsCreateTagModalOpen(false);
    setTagToDuplicate(null);
  };

  const handleReset = (e: MouseEvent) => {
    // reset to initial segment builder state
    e.preventDefault();
    setSegmentsData([{ id: ROOT_SEGMENT_ID, groupId: null, type: 'all', negation: false }]);

    if (cloneTag) {
      // If we have cloneTag set cloneTag data as default form values
      setValue('title', cloneTag.query.display_name);
      setValue('description', cloneTag.query.description);
    }
  };

  const toggleDescription = () => {
    setIsDescriptionVisible(!isDescriptionVisible);
  };

  const handleDateRangeChange = (val: IDropdownOption) => {
    if (val.value === 'custom') {
      datePickerRef.current?.setOpen(true);
    }

    setCampaignDuration(val as IDropdownOption);
  };

  const handleCalculateAvails = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!currentParams) return;
    initiateFlightpathAvails({ ...JSON.parse(currentParams) });
    setLastSentParams(currentParams);
  };

  const isFlightpathCalculationEnabled = useMemo(() => {
    return Boolean(
      startDate &&
        endDate &&
        insertion &&
        segmentsData.length > 1 &&
        !isLoading &&
        currentParams !== lastSentParams
    );
  }, [
    startDate,
    endDate,
    insertion,
    segmentsData,
    capDaysInterval,
    capMaxImpressions,
    isLoading,
    currentParams,
    lastSentParams,
  ]);

  const submitButtonCopy = useMemo(
    () =>
      cloneTag
        ? t('insights.integrations.megaphone.duplicate_tag_modal.cta')
        : t('insights.integrations.megaphone.create_tag_modal.cta'),
    [cloneTag]
  );

  useEffect(() => {
    if (cloneTag) {
      setValue('title', cloneTag?.query.display_name as string);

      if (cloneTag?.query?.description?.length) {
        setValue('description', cloneTag?.query.description as string);
        setIsDescriptionVisible(true);
      }
    }
  }, [cloneTag]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (cloneTag) {
      // we need to wait with setting the segments data until default values from url params are set
      timeout = setTimeout(() => {
        const tagSegmentsData = createQueryDataFromQuery([cloneTag.query.aql_query], null);
        setSegmentsData(tagSegmentsData);
      }, 0);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [cloneTag]);

  useEffect(() => {
    if (campaignDuration.value === 'custom') {
      datePickerRef.current?.setOpen(true);
    }

    if (campaignDuration.value === '30-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(30));
    }
    if (campaignDuration.value === '60-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(60));
    }
    if (campaignDuration.value === '90-days') {
      setStartDate(new Date());
      setEndDate(addDaysToDate(90));
    }
  }, [campaignDuration.value]);

  useEffect(() => {
    const [startCustomDate, endCustomDate] = dateRange;
    if (startCustomDate && endCustomDate) {
      const days = Math.ceil(
        (endCustomDate.getTime() - startCustomDate.getTime()) / (1000 * 3600 * 24)
      );

      setCampaignDuration({
        value: `${days}-days`,
        label: (
          <Option>
            {startCustomDate.toLocaleDateString()} - {endCustomDate.toLocaleDateString()}
          </Option>
        ),
      });
      setStartDate(startCustomDate);
      setEndDate(endCustomDate);
    }
  }, [dateRange]);

  useEffect(() => {
    if (!startDate || !endDate || !insertion || !(segmentsData.length > 1)) return;
    const builder = new AQLFlatQueryBuilder();
    buildQueryFromQueryData(groupQueryDataByGroupId(segmentsData), builder, null);
    const query = builder.build();

    const isORLogic = Boolean(insertion?.value && insertion.value.includes('_or'));
    const AdPlacementLogic: AdPlacementLogicType = isORLogic ? 'OR' : 'AND';

    let insertions: string[] = [];

    if (insertion?.value) {
      insertions = isORLogic
        ? insertion.value.replace('_or', '').split('-')
        : insertion.value.split('-');
    }

    const flightpath_params = {
      start_date: startDate.toISOString().substring(0, 10),
      end_date: endDate.toISOString().substring(0, 10),
      insertions,
      cap_days_interval: capDaysInterval,
      cap_max_impressions: capMaxImpressions,
      ad_placement_logic: AdPlacementLogic,
    };

    setCurrentParams(JSON.stringify({ flightpath_params, query }));
  }, [startDate, endDate, insertion, segmentsData, capDaysInterval, capMaxImpressions]);

  if (type === 'megaphone' && isFlightpathEnabled) {
    return (
      <Form onSubmit={handleSubmit(onSubmit)} data-testid='create-tag-form'>
        <FormInnerWrapper tw='flex flex-col xl:flex-row w-full xl:min-w-[800px]'>
          <FormInputsWrapper tw='flex flex-col w-full'>
            <Subtitle>Tag Query</Subtitle>
            <FormInputsContainer>
              <FormInputs>
                <FormLabel tw='pt-0' htmlFor='title'>
                  {t('insights.integrations.megaphone.create_tag_modal.title_input.label')}
                </FormLabel>
                <Spacer direction='vertical' size='xxs' />
                <Input
                  data-testid='create-tag-form-title'
                  tw='bg-page-background rounded focus:border-accent-muted focus:outline-none h-[35px]'
                  type='text'
                  id='title'
                  placeholder={t(
                    'insights.integrations.megaphone.create_tag_modal.title_input.placeholder'
                  )}
                  error={errors.title?.message}
                  {...register('title', { required: true })}
                />
                <Spacer direction='vertical' size='sm' />
                <AddDescriptionButton
                  onClick={toggleDescription}
                  type='button'
                  data-testid='create-tag-form-description-btn'>
                  {isDescriptionVisible ? (
                    <MinusIcon tw='stroke-text-and-icon-muted mr-[6px] w-4 h-4' />
                  ) : (
                    <PlusIcon tw='stroke-text-and-icon-muted mr-[6px] w-4 h-4' />
                  )}
                  {t('insights.integrations.megaphone.create_tag_modal.description_cta')}
                </AddDescriptionButton>
                {isDescriptionVisible && (
                  <>
                    <Spacer direction='vertical' size='xxs' />
                    <TextArea
                      data-testid='create-tag-form-description'
                      placeholder={t(
                        'insights.integrations.megaphone.create_tag_modal.description_input.placeholder'
                      )}
                      {...register('description', { required: false })}
                    />
                  </>
                )}
              </FormInputs>

              <SegmentCreator />
            </FormInputsContainer>
            <div tw='flex justify-between py-[12px] px-[24px] bg-neutral-background'>
              <ControlButton
                onClick={(e) => handleReset(e as unknown as MouseEvent)}
                tw='bg-transparent text-text-and-icon-muted text-xs'
                data-testid='create-tag-form-reset-btn'>
                {t('common.reset')}
              </ControlButton>
              <div tw='flex items-center text-sm gap-[4px] text-text-and-icon-muted'>
                {isTotalEpisodesCountLoading ? (
                  <Loader theme='light' size='sm' />
                ) : (
                  <span tw='text-accent-muted'>{totalEpisodeCount.toLocaleString('en-US')}</span>
                )}
                {t('insights.query_builder.footer_audio_message')}
              </div>
            </div>
          </FormInputsWrapper>
          <div tw='min-h-full w-[1px] bg-gray-1' />
          <div tw='flex flex-col justify-between'>
            <div tw='flex flex-col'>
              <Subtitle>Flightpath Parameters</Subtitle>
              <FormInputsContainer>
                <FormInputs tw='p-[8px] flex flex-col gap-[24px]'>
                  <div tw='relative w-full flex flex-col'>
                    <FormLabel htmlFor='startDate'>Duration</FormLabel>
                    <Spacer direction='vertical' size='xxs' />
                    <div tw='flex absolute h-10 w-10 z-10 top-[44px]'>
                      <DatePicker
                        tw='h-0 w-0 z-0 p-0 m-0'
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        selectsRange={true}
                        ref={datePickerRef}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        onChange={(update: [Date | null, Date | null]): void => {
                          setDateRange(update);
                        }}
                      />
                    </div>
                    <Dropdown
                      tw='h-[35px] w-full'
                      onOptionClick={(val) => handleDateRangeChange(val as IDropdownOption)}
                      onChange={() => {
                        // no need to do anything here
                      }}
                      value={campaignDuration}
                      options={campaignDurationOptions}
                      data-testid='create-tag-form-insertion'
                      id='duration'
                      dropdownMenuStyle={tw`w-full`}></Dropdown>
                  </div>

                  <div tw='flex flex-col w-full'>
                    <FormLabel htmlFor='insertion'>Insertion</FormLabel>
                    <Spacer direction='vertical' size='xxs' />
                    <Dropdown
                      tw='h-[35px]'
                      onChange={(val) => setInsertion(val as IDropdownOption)}
                      value={insertion}
                      options={insertionOptions}
                      data-testid='create-tag-form-insertion'
                      id='insertion'
                      dropdownMenuStyle={tw`w-full`}
                    />
                  </div>
                  <div>
                    <FormLabel htmlFor='capped' tw='text-[13px]'>
                      Frequency Cap
                      <span tw='text-text-and-icon-muted font-normal ml-1'>(optional)</span>
                    </FormLabel>
                    <div tw='text-xs whitespace-nowrap mt-2 text-text-and-icon'>
                      <span>Max of</span>
                      <Input
                        inline={true}
                        tw='w-[60px] max-w-[60px] h-[29px] input-no-arrows bg-neutral-background mx-[4px] placeholder:text-text-and-icon-muted'
                        type='number'
                        placeholder='#'
                        value={capMaxImpressions}
                        onChange={(e) =>
                          setCapMaxImpressions((e.currentTarget as HTMLInputElement).value)
                        }
                      />
                      <span>impresions every</span>
                      <Input
                        inline={true}
                        tw='w-[60px] max-w-[60px] h-[29px] input-no-arrows bg-neutral-background mx-[4px] placeholder:text-text-and-icon-muted'
                        type='number'
                        placeholder='#'
                        onChange={(e) =>
                          setCapDaysInterval((e.currentTarget as HTMLInputElement).value)
                        }
                      />
                      day(s)
                    </div>
                  </div>
                </FormInputs>
              </FormInputsContainer>
            </div>
            <div tw='flex justify-between py-[12px] px-[24px] bg-neutral-background'>
              <ControlButton
                disabled={!isFlightpathCalculationEnabled}
                onClick={(e: MouseEvent<HTMLButtonElement>) => handleCalculateAvails(e)}
                tw='border border-accent-muted text-xs text-accent-muted bg-page-background'
                css={[
                  !isFlightpathCalculationEnabled &&
                    tw`opacity-50 hover:opacity-50 cursor-not-allowed`,
                ]}
                data-testid=''>
                {lastSentParams ? 'Re-calculate Results' : 'Calculate Results'}
              </ControlButton>
              {(isNumber(totalAvails) || isLoading) && lastSentParams === currentParams && (
                <div tw='flex items-center text-sm gap-[4px] text-text-and-icon'>
                  {isLoading ? (
                    <Loader theme='light' size='sm' />
                  ) : (
                    <span tw='text-accent-muted text-text-and-icon'>
                      {Math.round(totalAvails || 0).toLocaleString('en-US')}
                    </span>
                  )}
                  {t('insights.integrations.megaphone.flightpath.footer_message')}
                </div>
              )}
            </div>
          </div>
        </FormInnerWrapper>

        <FormControlsContainer>
          <ButtonsWrapper>
            <ControlButton tw='bg-transparent text-text-and-icon-muted' onClick={handleCloseModal}>
              {t('common.cancel')}
            </ControlButton>
            <ControlButton
              tw='border-accent-2 bg-accent-2 text-text-and-icon-invert! w-[90px]'
              data-testid='create-tag-form-submit-btn'>
              {isSubmitting ? <Loader theme='dark' size='xs' /> : submitButtonCopy}
            </ControlButton>
          </ButtonsWrapper>
        </FormControlsContainer>
      </Form>
    );
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} data-testid='create-tag-form'>
        <FormInputsContainer>
          <FormInputs>
            <FormLabel htmlFor='title'>
              {t('insights.integrations.megaphone.create_tag_modal.title_input.label')}
            </FormLabel>
            <Spacer direction='vertical' size='xxs' />
            <Input
              data-testid='create-tag-form-title'
              tw='bg-page-background rounded focus:border-accent-muted focus:outline-none'
              type='text'
              id='title'
              placeholder={t(
                'insights.integrations.megaphone.create_tag_modal.title_input.placeholder'
              )}
              error={errors?.title?.message}
              {...register('title', { required: true })}
            />
            <Spacer direction='vertical' size='sm' />
            <AddDescriptionButton
              onClick={toggleDescription}
              type='button'
              data-testid='create-tag-form-description-btn'>
              {isDescriptionVisible ? (
                <MinusIcon tw='stroke-text-and-icon-muted mr-[6px] w-4 h-4' />
              ) : (
                <PlusIcon tw='stroke-text-and-icon-muted mr-[6px] w-4 h-4' />
              )}
              {t('insights.integrations.megaphone.create_tag_modal.description_cta')}
            </AddDescriptionButton>
            {isDescriptionVisible && (
              <>
                <Spacer direction='vertical' size='xxs' />
                <TextArea
                  data-testid='create-tag-form-description'
                  placeholder={t(
                    'insights.integrations.megaphone.create_tag_modal.description_input.placeholder'
                  )}
                  {...register('description', { required: false })}
                />
              </>
            )}
          </FormInputs>

          <SegmentCreator />
        </FormInputsContainer>
        <FormControlsContainer>
          <ControlButton
            onClick={(e) => handleReset(e as unknown as MouseEvent)}
            tw='bg-transparent text-text-and-icon-muted'
            data-testid='create-tag-form-reset-btn'>
            {t('common.reset')}
          </ControlButton>

          <ButtonsWrapper>
            <ControlButton tw='bg-transparent text-text-and-icon-muted' onClick={handleCloseModal}>
              {t('common.cancel')}
            </ControlButton>
            <ControlButton
              tw='border-accent-2 bg-accent-2 text-text-and-icon-invert! w-[90px]'
              data-testid='create-tag-form-submit-btn'>
              {isSubmitting ? <Loader theme='dark' size='xs' /> : submitButtonCopy}
            </ControlButton>
          </ButtonsWrapper>
        </FormControlsContainer>
      </Form>
    </>
  );
};
